'use client'

import { useState, useEffect, useMemo } from 'react'
import { useRouter, useSearchParams, usePathname } from 'next/navigation'
import { v4 as uuidv4 } from 'uuid'
import { buildNewUserMessage } from '@/lib/prompt-area/helper'
import { PromptAreaForm } from '@/components/prompt-area/prompt-area'
import { toast } from 'sonner'
import { motion, AnimatePresence } from "motion/react"
import { defaultOpacityAnimationInitial, defaultOpacityAnimationAnimate, defaultOpacityAnimationTransition } from '@/lib/const'
import { Message, SendNewMessage } from '@/lib/types'
import { ContentFile } from '@/lib/types/file-types'
import { useUserContext } from '@/components/providers'
import { useManager } from '@/hooks/use-manager'
import { usePromptForm } from '@/hooks/use-prompt-form'
// import { PageHeader, HeaderLeft, HeaderRight, NewChatButton, BookmarkButton } from "@/components/header/header"
import { Title } from "@/components/header/Title"
// static import of ConversationFlow
import { ConversationFlow } from '@/components/conversation-flow/ConversationFlow'
import { ConversationDisplay } from '@/components/chat/conversation-display'
// import { ConversationDisplay } from '@/components/chat/conversation-display-v2'
import { ErrorDisplay } from '@/components/chat/error'
import { ChatSkeleton } from '@/components/chat/skeleton'
import { Debug } from '@/components/auth/dev-features/debug'
import { cn } from '@/lib/utils'
import { AccessGate } from '../auth/access-gate'
import { useAppHotkeys } from '@/hooks/use-hotkeys'
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog'
import { BookmarkButton } from '@/components/header/bookmark-chat'
import { SidebarToggle } from '@/components/sidebar/sidebar-toggle'
import { useSidebar } from '@/components/sidebar/sidebar-provider'
import { useIsMobile } from '@/hooks/use-mobile'
import { Inset, InsetHeader, InsetContent } from '@/components/inset'
import { SquarePen } from 'lucide-react'
import { Button } from '@/components/ui/button'


// The actual interface component
export function ChatInterface({ onNewChat }: { onNewChat?: () => void }) {
    // Initialize hooks
    const { userContextValue } = useUserContext()
    const [lastMessageOfCurrentBranch, setLastMessageOfCurrentBranch] = useState<Message['id'] | null>(null)

    // Use Next.js pathname hook instead of window location
    const pathname = usePathname()
    const isOnOldChat = pathname && pathname.startsWith('/chat/') && pathname !== '/chat'

    // Initialize useManager hook
    const {
        requestInProgress,
        isBookmarked,
        handleBookmark,
        createNewRequest,
        addOrUpdateMessage,
        rerunRequest,
        messages,
        managerError,
        isInitializing,

        handleUpdateTitle,
        conversationTitle,
        conversationId,
        conversationSystemPrompt,
        setConversationSystemPrompt,
    } = useManager()

    // Initialize usePromptForm hook with the manager's handleSendingNewMessage
    const promptForm = usePromptForm({
        addOrUpdateMessage,
        createNewRequest,
        lastMessageId: lastMessageOfCurrentBranch
            ? lastMessageOfCurrentBranch
            : messages.length > 0
                ? messages.sort((a, b) => new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime())[messages.length - 1].id
                : null,
        messages,
        conversationSystemPrompt,
        setConversationSystemPrompt
    })

    // Determine if conversation has started based on message length or if on a chat URL
    const hasConversationStarted = messages.length > 0 || isOnOldChat

    // View toggle
    const [viewMode, setViewMode] = useState<'chat' | 'flow'>('chat')

    // Get first name of user and capitalize first letter
    const firstName = userContextValue?.firstName || userContextValue?.displayName?.split(' ')[0] || 'there'
    const capitalizedName = firstName === 'there' ? firstName : firstName.charAt(0).toUpperCase() + firstName.slice(1)



    // Show error state if there's a bad manager error that can't display messages
    if (hasConversationStarted && managerError) {
        return (
            <div className="w-full h-full flex items-center justify-center">
                <ErrorDisplay errorMessage={managerError} />
            </div>
        )
    }

    // Show loading state with skeleton UI when initializing a conversation
    if (isInitializing) {
        return (
            <ChatSkeleton />
        )
    }


    return (
            <Inset>
                <InsetHeader breadcrumbs={['Chat', conversationTitle || 'New chat']}>
                    <AccessGate>
                        {messages.length > 0 && (
                            <BookmarkButton
                                isBookmarked={isBookmarked}
                                onBookmark={() => handleBookmark('conversation')}
                            />
                        )}
                    </AccessGate>
                    {/* <div className="flex items-center gap-2">
                        <Button
                            variant={viewMode === 'chat' ? 'default' : 'outline'}
                            size="sm"
                            onClick={() => setViewMode('chat')}
                        >
                            Chat
                        </Button>
                        <Button
                            variant={viewMode === 'flow' ? 'default' : 'outline'}
                            size="sm"
                            onClick={() => setViewMode('flow')}
                        >
                            Tree
                        </Button>
                    </div> */}
                    <div className="md:hidden block cursor-pointer" onClick={() => window.location.href = '/'}>
                        <SquarePen className="h-4 w-4" />
                    </div>
                    <div className="hidden md:flex items-center gap-4">
                        <AccessGate 
                            authFallback={
                                <div className="flex items-center gap-2">
                                    <Button
                                        variant="outline"
                                        size="sm"
                                        onClick={() => window.location.href = '/auth/log-in'}
                                    >
                                        Log In
                                    </Button>
                                    <Button
                                        variant="default"
                                        size="sm"
                                        onClick={() => window.location.href = '/auth/sign-up'}
                                    >
                                        Sign Up
                                    </Button>
                                </div>
                            }
                        >
                            <div className="hidden" />
                        </AccessGate>
                    </div>
                </InsetHeader>

                {/* Display messages debug info */}
                <AccessGate developerFeature="messageArray">
                    <div className="w-full absolute z-50">
                        <Debug messages={messages} />
                    </div>
                </AccessGate>

                <InsetContent 
                    withPadding={false} 
                    className="pt-0"
                >
                    {!hasConversationStarted && (
                        <motion.div
                            initial={{ opacity: 0, y: 20 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.5, ease: 'easeInOut' }}
                            className="text-center space-y-2 mb-16 md:mb-16 pt-16 md:pt-28"
                        >
                            <h1 className="text-3xl md:text-4xl font-bold text-foreground">Hi {capitalizedName}!</h1>
                            <p className="text-lg md:text-xl text-muted-foreground">&quot;What&apos;s on your mind today?&quot;</p>
                        </motion.div>
                    )}

                    {hasConversationStarted && viewMode === 'chat' && (
                        <ConversationDisplay
                            messages={messages}
                            onUseMessageContent={() => { }}
                            rerunRequest={rerunRequest}
                            onEdit={promptForm.handleEditMessage}
                            handleBookmark={handleBookmark}
                            onBranchSwitch={setLastMessageOfCurrentBranch}
                            className={cn(
                                "transition-opacity duration-200 ease-in-out",
                                promptForm.isEditing && "opacity-30"
                            )}
                        />
                    )}
                    {hasConversationStarted && viewMode === 'flow' && (
                        <div className="w-full h-full min-h-[calc(100vh-6rem)]">
                            <ConversationFlow
                                messages={messages}
                                onCreateMessage={(parentId, content) => {
                                    
                                    const model = promptForm.form.getValues('model')
                                    const newMessage = buildNewUserMessage({
                                        data: { mode: 'text', prompt: content, model, files: [] },
                                        parentId
                                    })
                                    addOrUpdateMessage(newMessage)
                                    createNewRequest({ userMessage: newMessage, onSuccess: () => {}, onFailure: () => {} })
                                }}
                            />
                        </div>
                    )}
                    {viewMode === 'chat' && (
                        <PromptAreaForm
                            promptForm={promptForm}
                            messageCount={messages.length}
                            requestInProgress={requestInProgress}
                            conversationSystemPrompt={conversationSystemPrompt}
                            setConversationSystemPrompt={setConversationSystemPrompt}
                        />
                    )}
                </InsetContent>
            </Inset>

        
    )
} 